<template>
	<div class="modern-filter-wrapper">
		<!-- Filter Chips -->
		<FilterChips
			v-if="activeFilters.length > 0"
			:filters="filtersObject"
			:filter-items="filterItems"
			:filter-display-values="filterDisplayValues"
			@remove="removeFilter"
			@edit="editFilter"
		/>

		<!-- Filter Dropdown -->
		<div class="dropdown modern-filter-dropdown" ref="dropdownRef">
			<button
				class="filter-tab"
				type="button"
				data-bs-toggle="dropdown"
				data-bs-auto-close="outside"
				aria-expanded="false"
				ref="dropdownButton"
			>
				<font-awesome-icon :icon="['fa', 'filter']" class="me-2" />
				<span class="filter-tab-text">Filter</span>
			</button>
			<div class="dropdown-menu p-0" style="min-width: 280px" @click.stop>
				<!-- Step 1: Filter Type Selection -->
				<div v-if="!selectedFilterType" class="filter-type-list">
					<div
						v-for="item in visibleFilterItems"
						:key="item.name"
						class="filter-type-item"
						@click="selectFilterType(item)"
					>
						<font-awesome-icon v-if="item.icon" :icon="['fa', item.icon.replace('fad fa-', '')]" class="me-2" />
						<span>{{ item.label }}</span>
						<font-awesome-icon :icon="['fa', 'chevron-right']" class="ms-auto arrow-right" />
					</div>
					<div v-if="visibleFilterItems.length === 0" class="text-muted small text-center py-3">
						No filters available
					</div>
				</div>

				<!-- Step 2: Filter Value Selection -->
				<div v-else class="filter-value-selection">
					<div class="filter-content p-3">
						<component
							:is="getComponentName(selectedFilterType.component)"
							:item="tempFilterItem"
							@update="updateTempValue"
						/>
					</div>
					<div class="filter-footer row gx-2">
						<button v-if="!isEditMode" class="btn col btn-sm btn-outline-secondary" @click="goBack">
							Back
						</button>
						<button class="btn col btn-sm btn-primary" @click="applyFilter">Apply</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import SelectItem from './filters/SelectItem.vue'
import MonthItem from './filters/MonthItem.vue'
import InputItem from './filters/InputItem.vue'
import DateRangeItem from './filters/DateRangeItem.vue'
import FilterChips from './FilterChips.vue'
import { Dropdown } from 'bootstrap'

export default {
	name: 'ModernFilter',
	components: {
		SelectItem,
		MonthItem,
		InputItem,
		DateRangeItem,
		FilterChips,
	},
	props: {
		filterItems: {
			type: Array,
			default: () => [],
		},
		sortAlphabetically: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			selectedFilterType: null,
			tempFilterItem: null,
			dropdownInstance: null,
			isEditMode: false,
			filterDisplayValues: {},
		}
	},
	computed: {
		visibleFilterItems() {
			const filtered = this.filterItems.filter((item) => {
				if (item.visible === false) return false
				if (item.component === 'SelectItem' && (!item.options || item.options.length === 0)) {
					return false
				}
				const value = item.value
				if (value !== null && value !== undefined && value !== '') {
					return false
				}
				return true
			})

			if (this.sortAlphabetically) {
				return filtered.sort((a, b) => {
					const labelA = (a.label || '').toLowerCase()
					const labelB = (b.label || '').toLowerCase()
					return labelA.localeCompare(labelB)
				})
			}

			return filtered
		},
		activeFilters() {
			return this.filterItems.filter((item) => {
				const value = item.value
				return value !== null && value !== undefined && value !== ''
			})
		},
		filtersObject() {
			const obj = {}
			this.filterItems.forEach((item) => {
				obj[item.name] = item.value
			})
			return obj
		},
	},
	mounted() {
		if (this.$refs.dropdownButton) {
			this.dropdownInstance = new Dropdown(this.$refs.dropdownButton)
		}

		this.$refs.dropdownRef?.addEventListener('hidden.bs.dropdown', () => {
			this.selectedFilterType = null
			this.tempFilterItem = null
			this.isEditMode = false

			if (this.dropdownInstance?._popper && this.$refs.dropdownButton) {
				this.dropdownInstance._popper.state.elements.reference = this.$refs.dropdownButton
			}
		})

		this.loadMissingDisplayValues()
	},
	watch: {
		filterItems: {
			handler() {
				this.loadMissingDisplayValues()
			},
			deep: true,
		},
	},
	methods: {
		getComponentName(component) {
			if (!component) return 'InputItem'
			const componentMap = {
				select: 'SelectItem',
				month: 'MonthItem',
				input: 'InputItem',
				'date-range': 'DateRangeItem',
			}
			return componentMap[component.toLowerCase()] || component
		},
		selectFilterType(item) {
			this.isEditMode = false
			this.selectedFilterType = item
			this.tempFilterItem = { ...item, value: item.value || '' }
		},
		goBack() {
			this.isEditMode = false
			this.selectedFilterType = null
			this.tempFilterItem = null
		},
		updateTempValue(value) {
			if (this.tempFilterItem) {
				this.tempFilterItem.value = value
			}
		},
		applyFilter() {
			if (this.tempFilterItem && this.selectedFilterType) {
				const value = this.tempFilterItem.value
				let emitValue = value

				if (typeof value === 'object' && value?.id) {
					this.filterDisplayValues[this.selectedFilterType.name] = value
					emitValue = value.id
				} else if (!value) {
					delete this.filterDisplayValues[this.selectedFilterType.name]
				}

				this.$emit('update:filter', {
					name: this.selectedFilterType.name,
					value: emitValue,
				})

				this.dropdownInstance?.hide()

				this.selectedFilterType = null
				this.tempFilterItem = null
			}
		},
		cancelSelection() {
			this.dropdownInstance?.hide()
			this.selectedFilterType = null
			this.tempFilterItem = null
		},
		removeFilter(filterName) {
			delete this.filterDisplayValues[filterName]

			this.$emit('update:filter', {
				name: filterName,
				value: '',
			})
		},
		editFilter(filterName, event) {
			const filterItem = this.filterItems.find((item) => item.name === filterName)
			if (!filterItem) {
				console.warn('Filter item not found:', filterName)
				return
			}

			this.isEditMode = true

			this.selectedFilterType = filterItem
			this.tempFilterItem = { ...filterItem, value: filterItem.value || '' }

			this.$nextTick(() => {
				if (this.dropdownInstance) {
					try {
						this.dropdownInstance.show()

						if (event && event.target) {
							const chipElement = event.target.closest('.filter-chip') || event.target

							this.$nextTick(() => {
								if (this.dropdownInstance._popper) {
									this.dropdownInstance._popper.state.elements.reference = chipElement
									this.dropdownInstance._popper.update()
								}
							})
						}
					} catch (error) {
						console.error('Error showing dropdown:', error)
					}
				}
			})
		},
		async loadMissingDisplayValues() {
			// Not needed for Juniper clients
		},
	},
}
</script>

<style lang="scss" scoped>
.modern-filter-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
}

.modern-filter-dropdown {
	position: relative;

	.dropdown-menu {
		overflow: visible !important;
		border-radius: 8px;
		box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
		border: 1px solid #e5e7eb;
	}

	.filter-tab {
		background-color: #e5e7eb;
		color: #6b7280;
		border: none;
		font-weight: 600;
		padding: 8px 16px;
		font-size: 14px;
		cursor: pointer;
		border-radius: 20px;
		transition: all 0.2s ease;
		display: flex;
		align-items: center;
		gap: 8px;

		&:hover {
			background-color: #d1d5db;
			color: #374151;
		}
	}
}

.filter-type-list {
	padding: 0.5rem 0;
}

.filter-type-item {
	display: flex;
	align-items: center;
	padding: 0.75rem 1rem;
	cursor: pointer;
	transition: background-color 0.15s ease;
	font-size: 0.9rem;

	.arrow-right {
		opacity: 0;
		transition: opacity 0.15s ease;
	}

	&:hover {
		background-color: #f8f9fa;

		.arrow-right {
			opacity: 1;
		}
	}
}

.filter-type-item svg,
.filter-type-item i {
	font-size: 0.9rem;
	color: #6c757d;
}

.filter-type-item .arrow-right {
	opacity: 0;
	transition: opacity 0.15s ease;
	font-size: 0.8rem;
	width: 0.8rem;
	height: 0.8rem;
}

.filter-type-item:hover .arrow-right {
	opacity: 1;
}

.filter-value-selection {
	display: flex;
	flex-direction: column;
}

.filter-content {
	flex: 1;
	overflow-y: auto;
	max-height: 300px;
}

.filter-footer {
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
	padding: 0.75rem 1rem;
	border-top: 1px solid #e9ecef;
}
</style>
